import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../../global/transform-tag-name';
import { SharedScrollLockService } from '../../../global/init-services';
import { getRandomId } from '../../../utils/get-random-id';
import { MediaQueryChangeEvent, MEDIA_QUERY_SIZES } from '../../../utils/services/media-query.service';

/**
 * @slot header - the accordion item header
 * @slot title - the accordion item headline
 * @slot subtitle - the accordion item subtitle
 * @slot icon - the accordion item header icon
 * @slot counter - the accordion item header counter
 * @slot mobile-header - the accordion item mobile-header
 * @slot content - the main content of the accordion
 */
@Component({
  tag: 'wb-accordion-item',
  styleUrl: 'accordion-item.scss',
  shadow: true,
})
export class AccordionItemComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** @internal If present, uses the subtle variant of the accordion */
  @Prop({ reflect: true })
  public subtle?: boolean;

  /** If present, opens up the accordion item */
  @Prop({ reflect: true, mutable: true })
  public open: boolean = false;

  /** Aria label for button responsible to expand/collapse item */
  @Prop({ reflect: true, mutable: true })
  public buttonActionLabel: string;

  /** Aria label for button responsible to close item */
  @Prop({ reflect: true, mutable: true })
  public closeAriaLabel: string;

  @State()
  private maxHeight: string = '0';

  /** Event emitted when accordion-item opens */
  @Event()
  public wbaccordionitemopen: EventEmitter<void>;

  /**
   * @deprecated Use `wbaccordionitemopen` instead
   * Event emitted when accordion-item opens
   */
  @Event()
  public wbopen: EventEmitter<void>;

  /** Event emitted when accordion-item closes */
  @Event()
  public wbaccordionitemclose: EventEmitter<void>;

  /**
   * @deprecated Use `wbaccordionitemclose` instead
   * Event emitted when accordion-item closes
   */
  @Event()
  public wbclose: EventEmitter<void>;

  /** toggles accordion item between open and closed */
  @Method()
  public async toggle(): Promise<void> {
    this.open = !this.open;
  }

  /** collapses accordion item */
  @Method()
  public async collapse(): Promise<void> {
    this.open = false;
  }

  /** expands accordion item */
  @Method()
  public async expand(): Promise<void> {
    this.open = true;
  }

  @Listen('click')
  public onClick(event: MouseEvent): void {
    const path = event.composedPath();

    if (path.includes(this.toggleButtonEl) || path.includes(this.mobileHeaderCloseButtonEl)) {
      this.toggle();
    }
  }

  @Listen('wbresize', { target: 'window' })
  public onChange(event: CustomEvent<MediaQueryChangeEvent>): void {
    const { previous, current } = event.detail;
    const changedToMobileVariant = previous > 'mq3' && current <= 'mq3';
    const changedToDesktopVariant = previous <= 'mq3' && current > 'mq3';

    if (changedToMobileVariant || changedToDesktopVariant) {
      this.collapse();
    }
  }

  @Watch('open')
  public onOpenChange(): void {
    const scrollHeight = this.sectionEl.scrollHeight ? `${this.sectionEl.scrollHeight}px` : 'auto';

    if (this.open) {
      this.setMaxHeight(scrollHeight);
      this.wbaccordionitemopen.emit();
      this.wbopen.emit();
      // Ensure setOverflow is called even if transitionend doesn't fire (e.g., when prefers-reduced-motion is active)
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        this.setOverflow();
      }
    } else {
      this.setOverflow();

      // Handling transition animation while closing by switching from the scroll height to 0 within each animation frame
      requestAnimationFrame(() => {
        this.setMaxHeight(scrollHeight);
        requestAnimationFrame(() => {
          this.setMaxHeight('0');
        });
      });

      SharedScrollLockService.unlock(this.uniqueId);
      this.wbaccordionitemclose.emit();
      this.wbclose.emit();
    }
  }

  private accordionEl: HTMLWbAccordionElement;
  private toggleButtonEl!: HTMLElement;
  private mobileHeaderCloseButtonEl!: HTMLElement;
  private sectionEl!: HTMLElement;
  private headerEl!: HTMLElement;
  private uniqueId: string = getRandomId();
  private hasOverflow: boolean;
  private TagNames = transformTagNames(this, 'wb-accordion-item', ['wb-button', 'wb-round-button', 'wb-text', 'wb-icon']);

  public connectedCallback(): void {
    this.accordionEl = this.el.parentElement as HTMLWbAccordionElement;

    if (this.accordionEl.preventMobileFlyout) {
      this.el.setAttribute('prevent-mobile-flyout', 'prevent-mobile-flyout');
    }

    this.subtle = this.accordionEl.subtle;

    if (this.open && this.hasMobileFlyout()) {
      // Lock the scroll if the accordion-item is open and the mobile flyout is active
      SharedScrollLockService.lock(this.uniqueId);
    }
  }

  public disconnectedCallback(): void {
    SharedScrollLockService.unlock(this.uniqueId);
  }

  public componentDidLoad(): void {
    this.onOpenChange();
  }

  private setOverflow = (): void => {
    this.hasOverflow = this.open;
  };

  private setMaxHeight = (height: string): void => {
    this.maxHeight = height;
  };

  private onSectionTransitionEnd = (): void => {
    let height = 'auto';

    if (this.open && this.hasMobileFlyout()) {
      height = `${window.innerHeight - this.headerEl.offsetHeight}px`;
      SharedScrollLockService.lock(this.uniqueId);
    }

    if (this.open) {
      this.setOverflow();
      this.setMaxHeight('auto');
    }

    this.el.style.setProperty('--content-height', height);
  };

  /**
   * Checks if the accordion item has a mobile flyout.
   * @returns {boolean} Returns true if the window width is less than MEDIA_QUERY_SIZES.mq4 and the accordion element does not prevent mobile flyout, otherwise returns false.
   */
  private hasMobileFlyout = (): boolean => {
    return window.innerWidth < MEDIA_QUERY_SIZES.mq4 && !this.accordionEl.preventMobileFlyout;
  };

  private bindToggleButton = (el: HTMLDivElement): void => {
    this.toggleButtonEl = el;
  };

  private bindCloseButton = (el: HTMLWbButtonElement) => {
    this.mobileHeaderCloseButtonEl = el;
  };

  private bindSectionEl = (el: HTMLElement) => {
    this.sectionEl = el;
  };

  private bindHeaderEl = (el: HTMLElement) => {
    this.headerEl = el;
  };

  public render(): JSX.Element {
    const { WbButton, WbRoundButton, WbText, WbIcon } = this.TagNames;

    return (
      <Host class="wb-accordion-item">
        <div class="toggle" ref={this.bindToggleButton}>
          <WbText size="l" strong class="header" tag="div">
            <slot name="header">
              <div class="inner">
                <div class="header-icon">
                  <slot name="icon" />
                </div>
                <div class="header-content">
                  <div class="headline">
                    <slot name="title" />
                    <slot name="counter" />
                  </div>
                  <WbText size="l" class="subtitle">
                    <slot name="subtitle" />
                  </WbText>
                </div>
              </div>
            </slot>
          </WbText>

          <WbRoundButton class="round-button" size="new-m" aria-expanded={this.open} aria-controls={this.uniqueId} button-aria-label={this.buttonActionLabel}>
            <WbIcon flip-rtl class="icon" name="chevron-right/24" aria-hidden="true" />
          </WbRoundButton>
        </div>
        <div class={{ section: true, 'has-overflow': this.hasOverflow }} id={this.uniqueId} onTransitionEnd={this.onSectionTransitionEnd} ref={this.bindSectionEl} style={{ '--section-max-height': this.maxHeight }}>
          <div class="mobile-header" ref={this.bindHeaderEl} aria-hidden={`${!this.open}`} inert={!this.open}>
            <WbButton class="close" variant="tertiary" size="new-m" aria-label={this.closeAriaLabel} ref={this.bindCloseButton}>
              <WbIcon aria-hidden="true" flip-rtl name="chevron-left/24" />
              <WbText size="l" strong>
                <slot name="mobile-header" />
              </WbText>
            </WbButton>
          </div>
          <div class="content" aria-hidden={`${!this.open}`} inert={!this.open}>
            <WbText size="l">
              <slot name="content" />
            </WbText>
          </div>
        </div>
      </Host>
    );
  }
}
